import PropTypes from 'prop-types';
import { Box, Typography, LinearProgress, Skeleton, Button } from '@mui/material';
import useSalesforceRaiseMetrics from 'hooks/useSalesforceRaiseMetrics';
import { formatCurrency } from 'utils/utils';

const PLACEHOLDER = '—';

const ERROR_MESSAGES = {
	not_connected: 'Connect Salesforce to view raise metrics.',
	access_denied: "You don't have access to these Salesforce metrics.",
	unavailable: 'Unable to load metrics',
};

// Compact currency (612000000 -> "$612M"); falls back to the placeholder when absent.
const formatMoney = (value) =>
	value === null || value === undefined
		? PLACEHOLDER
		: formatCurrency(value, 'USD', { compact: true });

// "3 Initial Contact · 1 PPM Sent · ..." from the by_stage map; null when empty.
const buildPipelineSummary = (byStage) => {
	const entries = Object.entries(byStage || {});
	if (entries.length === 0) return null;
	return entries.map(([stage, { count }]) => `${count} ${stage}`).join(' · ');
};

const cardSx = {
	backgroundColor: 'background.subtle',
	borderRadius: '7px',
	padding: '9px 11px',
};

const containerSx = {
	flex: 1,
	overflowY: 'auto',
	padding: '13px 15px',
	display: 'flex',
	flexDirection: 'column',
	gap: '11px',
};

const KpiCard = ({ label, value, sub, subColor = 'text.secondary' }) => (
	<Box sx={cardSx}>
		<Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
			{label}
		</Typography>
		<Typography sx={{ fontSize: '16px', fontWeight: 600, color: 'text.primary' }}>
			{value}
		</Typography>
		{sub && (
			<Typography sx={{ fontSize: '11px', color: subColor }}>{sub}</Typography>
		)}
	</Box>
);

KpiCard.propTypes = {
	label: PropTypes.string.isRequired,
	value: PropTypes.node.isRequired,
	sub: PropTypes.node,
	subColor: PropTypes.string,
};

const RaiseMetricsSkeleton = () => (
	<Box sx={containerSx} data-testid='raise-metrics-skeleton'>
		<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
			{[0, 1, 2].map((i) => (
				<Skeleton key={i} variant='rounded' height={62} sx={{ borderRadius: '7px' }} />
			))}
		</Box>
		<Skeleton variant='rounded' height={24} sx={{ borderRadius: '3px' }} />
		<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
			{[0, 1, 2].map((i) => (
				<Skeleton key={i} variant='rounded' height={62} sx={{ borderRadius: '7px' }} />
			))}
		</Box>
		<Skeleton variant='rounded' height={34} sx={{ borderRadius: '7px' }} />
	</Box>
);

const RaiseMetricsError = ({ message, onRetry }) => (
	<Box
		sx={{
			...containerSx,
			alignItems: 'center',
			justifyContent: 'center',
			gap: '12px',
			cursor: 'default',
		}}
	>
		<Typography variant='body2' color='error'>
			{message}
		</Typography>
		<Button variant='outlined' size='small' onClick={onRetry}>
			Retry
		</Button>
	</Box>
);

RaiseMetricsError.propTypes = {
	message: PropTypes.string.isRequired,
	onRetry: PropTypes.func.isRequired,
};

const ActiveRaiseContent = ({ entityId, onOpenFundV }) => {
	const { metrics, loading, error, refetch } = useSalesforceRaiseMetrics(entityId);

	if (error) {
		return (
			<RaiseMetricsError
				message={ERROR_MESSAGES[error] || ERROR_MESSAGES.unavailable}
				onRetry={refetch}
			/>
		);
	}

	// Covers loading, the initial mount, and "no fund selected yet".
	if (loading || !metrics) {
		return <RaiseMetricsSkeleton />;
	}

	const {
		entity_name: entityName,
		target_size: targetSize,
		total_commitments: totalCommitments,
		pct_to_target: pctToTarget,
		investor_count: investorCount,
		recent_commitments_7d: recentCommitments,
		fundraising_pipeline: pipeline,
	} = metrics;

	const recentSub =
		recentCommitments === null || recentCommitments === undefined
			? null
			: `+${formatMoney(recentCommitments)} this week`;
	const pipelineSummary =
		buildPipelineSummary(pipeline?.by_stage) || 'No active pipeline';
	const hasTargetProgress =
		pctToTarget !== null && pctToTarget !== undefined;

	return (
		<Box sx={{ ...containerSx, cursor: 'pointer' }} onClick={onOpenFundV}>
			{/* kpi row 1 */}
			<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
				<KpiCard
					label='Committed'
					value={formatMoney(totalCommitments)}
					sub={recentSub}
					subColor='score.up'
				/>
				<KpiCard
					label='LPs committed'
					value={investorCount ?? PLACEHOLDER}
					sub={pipelineSummary}
				/>
				<KpiCard label='Re-up rate' value={PLACEHOLDER} />
			</Box>

			{/* progress bar — % to target (hidden when there is no target) */}
			{hasTargetProgress && (
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							fontSize: '11px',
							color: 'text.secondary',
						}}
					>
						<span>To target ({formatMoney(targetSize)})</span>
						<Typography
							component='span'
							sx={{ fontSize: '11px', fontWeight: 600, color: 'text.primary' }}
						>
							{pctToTarget}%
						</Typography>
					</Box>
					<LinearProgress
						variant='determinate'
						value={Math.min(pctToTarget, 100)}
						sx={{
							height: 6,
							borderRadius: 3,
							backgroundColor: 'background.subtle',
							'& .MuiLinearProgress-bar': {
								backgroundColor: 'signal.amber',
								borderRadius: 3,
							},
						}}
					/>
				</Box>
			)}

			{/* kpi row 2 — metrics not yet sourced from the backend */}
			<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
				<KpiCard label='First close' value={PLACEHOLDER} />
				<KpiCard label='Confidence' value={PLACEHOLDER} sub='AIMe · daily' />
				<KpiCard label='VDR signals' value={PLACEHOLDER} />
			</Box>

			{/* enter fund raise CTA */}
			<Box
				sx={{
					padding: '8px',
					textAlign: 'center',
					fontSize: '12px',
					fontWeight: 500,
					color: 'status.blue.text',
					border: '0.5px solid',
					borderColor: 'status.blue.border',
					borderRadius: '7px',
					cursor: 'pointer',
					background: 'transparent',
					mt: '2px',
					'&:hover': { backgroundColor: 'status.blue.bg' },
				}}
			>
				Enter {entityName} raise dashboard →
			</Box>
		</Box>
	);
};

ActiveRaiseContent.propTypes = {
	entityId: PropTypes.string,
	onOpenFundV: PropTypes.func.isRequired,
};

export default ActiveRaiseContent;
